import { findLastIndex } from "@aprao/common/src/array/findLastIndex.js";
import { removeRightWhile } from "@aprao/common/src/array/removeRightWhile.js";
import {
  type BigFraction,
  bigFractionFromFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import {
  FRACTION_ONE,
  FRACTION_ZERO,
  fractionLt,
  fractionLte,
  fractionMin,
} from "@aprao/common/src/math/fraction.js";
import { RtPositiveInteger } from "@aprao/common/src/runtypes/common.js";
import type {
  Fraction,
  WithProjectionPeriods,
} from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable } from "mobx";
import { createErr, createOk } from "option-t/plain_result";

import { MAX_PERIOD_COUNT, RtPeriodIndex } from "./utils.js";

interface SpreadByDurationParams {
  // allow using nominal typing to mark validated parameters
  __spreadByDurationParams: true;
  periodIndex: number;
  duration: number;
}

interface AdjustPeriodParams {
  __adjustPeriodParams: true;

  periodIndex: number;
  portion: BigFraction | null;
  totalAmountHint: BigFraction;
}

/**
 * Build an array of ratio per period.
 *
 * The sum or part can exceed 1 in some case (due to the way the easing
 * function works). It is up to the calling site to adjust the values.
 *
 * @param length count of period
 * @param easingFunction the function to spread the parts ratios
 */
const makePortionsPerPeriod = (
  length: number,
  easingFunction: (t: number) => number,
) => {
  const partsOfTotalPerPeriod: Array<BigFraction> = [];
  times(length, (idx) =>
    bigFractionFromNumber(easingFunction((idx + 1) / length)),
  ).reduce((prev, curr) => {
    partsOfTotalPerPeriod.push(curr.sub(prev));
    return curr;
  }, FRACTION_ZERO);

  return partsOfTotalPerPeriod;
};

/**
 * Manages the `target`'s sparse array of portions indexed by periods' index.
 * The periods without portions assigned are set to `null`.
 *
 * We use an array here since this is the only native JSON compatible structure
 * that ensures uniqueness of numeric index. That said, MobX doesn't like
 * accessing and setting index out of bounds, so we manually resize the array
 * to avoid issues.
 */
export class WithProjectionPeriodsDelegate {
  readonly $model;

  constructor(model: WithProjectionPeriods) {
    makeAutoObservable(this);

    this.$model = model;
  }

  get duration() {
    return this.$model.projectionPeriods.length;
  }

  get portionAssignable() {
    return this.portionAssignableFromPeriodIndex(Number.POSITIVE_INFINITY);
  }

  getPortionAtPeriodIndex(periodIndex: number) {
    if (this.$model.projectionPeriods.length <= periodIndex) {
      return null;
    }

    const portion = this.$model.projectionPeriods[periodIndex];
    return portion == null ? null : bigFractionFromFraction(portion);
  }

  /**
   * Returns the portion of the total available to assign from the given period
   * index (included).
   */
  portionAssignableFromPeriodIndex(periodIndex: number) {
    return this.$model.projectionPeriods
      .slice(0, periodIndex)
      .reduce(
        (sum: BigFraction, portion) =>
          portion == null ? sum : sum.sub(portion),
        FRACTION_ONE,
      );
  }

  _setPortionAtPeriodIndex(periodIndex: number, portion: Fraction | null) {
    if (this.$model.projectionPeriods.length <= periodIndex) {
      this.$model.projectionPeriods.push(
        ...times(
          periodIndex - this.$model.projectionPeriods.length + 1,
          () => null,
        ),
      );
    }

    this.$model.projectionPeriods[periodIndex] = portion;

    // Maintain the portions array in good shape by trimming `null` tail when
    // necessary. Setting a period to `null` may have created a tail of
    // continuous `null`.
    removeRightWhile(
      this.$model.projectionPeriods,
      (_portion) => _portion != null,
    );
  }

  validateAdjustPeriod(
    params: {
      periodIndex: number;
      totalAmountHint: BigFraction;
    } & (
      | { amount: number; portion: null }
      | { amount: null; portion: BigFraction }
      | { amount: null; portion: null }
    ),
  ) {
    const parsedPeriodIndex = RtPeriodIndex.safeParse(params.periodIndex);
    if (
      parsedPeriodIndex.success &&
      !FRACTION_ZERO.equals(params.totalAmountHint)
    ) {
      const portion =
        params.portion ??
        (params.amount != null ?
          bigFractionFromNumber(params.amount).div(params.totalAmountHint)
        : null);

      if (portion == null || fractionLte(portion, FRACTION_ONE)) {
        return createOk<AdjustPeriodParams>({
          __adjustPeriodParams: true,
          periodIndex: parsedPeriodIndex.data,
          portion,
          totalAmountHint: params.totalAmountHint,
        });
      }
    }

    return createErr("Invalid parameters.");
  }

  adjustPeriod(params: AdjustPeriodParams) {
    if (params.portion == null) {
      // remove the installment
      this._setPortionAtPeriodIndex(params.periodIndex, null);
    } else {
      const indexStartTail = params.periodIndex + 1;
      const portionAssignable = this.portionAssignableFromPeriodIndex(
        params.periodIndex,
      );
      const portionAdjustedForSpot = fractionMin(
        portionAssignable,
        params.portion,
      );

      this._setPortionAtPeriodIndex(
        params.periodIndex,
        portionAdjustedForSpot.toFraction(),
      );

      // now we check if we need to re-scale the portions in the tail
      const portionAssignableForTail = portionAssignable.sub(
        portionAdjustedForSpot,
      );
      const portionTail = this.$model.projectionPeriods
        .slice(indexStartTail)
        .reduce(
          (sum: BigFraction, portion) =>
            portion == null ? sum : sum.add(portion),
          FRACTION_ZERO,
        );
      const shouldScaleTail = fractionLt(portionAssignableForTail, portionTail);

      if (shouldScaleTail) {
        const ratioTail = portionAssignableForTail.div(portionTail);
        const tailAmountHint = portionAssignableForTail.mul(
          params.totalAmountHint,
        );

        const indexEndTail = findLastIndex(
          this.$model.projectionPeriods,
          (portion) => portion != null,
        );

        // Slice the portions to only treat the tail until the last assigned
        // period (don't continue after).
        const tailTrimmed = this.$model.projectionPeriods.slice(
          indexStartTail,
          indexEndTail + 1,
        );

        const checkTotal = tailTrimmed.reduce(
          // eslint-disable-next-line max-params
          (sum, prevPortion, idx, { length }) => {
            if (prevPortion == null) {
              return sum;
            }

            const isLast = idx + 1 === length;

            // Re-adjust the ratio to get a resulting amount that only has two
            // decimal.
            // Doing so we might get some left over, so we make sure the last
            // period completes the sum.
            const amount =
              isLast ?
                tailAmountHint.sub(sum)
              : ratioTail.mul(prevPortion).mul(params.totalAmountHint).floor(2);

            this.$model.projectionPeriods[idx + indexStartTail] = amount
              .div(params.totalAmountHint)
              .toFraction();

            return sum.add(amount);
          },
          FRACTION_ZERO,
        );

        // sanity check, should never happen
        if (!checkTotal.equals(tailAmountHint)) {
          throw new Error(
            `The sum of the portions should be ${tailAmountHint.valueOf()} (found ${checkTotal.valueOf()})`,
          );
        }
      }
    }
  }

  validateSpreadByDuration(periodIndex: number, duration: number) {
    const parsedPeriodIndex = RtPeriodIndex.safeParse(periodIndex);
    const parsedDuration = RtPositiveInteger.safeParse(duration);

    if (
      parsedPeriodIndex.success &&
      parsedDuration.success &&
      periodIndex + duration <= MAX_PERIOD_COUNT
    ) {
      return createOk<SpreadByDurationParams>({
        __spreadByDurationParams: true,
        periodIndex: parsedPeriodIndex.data,
        duration: parsedDuration.data,
      } as const);
    } else {
      return createErr("Invalid spread parameters.");
    }
  }

  /**
   * Spread the portions from `periodIndex` for the given `duration`.
   *
   * The distribution of the portion is tweaked by the given `easingFunction`.
   * The portions are readjusted against the `totalAmountHint` to make sure once
   * applied to the real value they yield a number rounded to 2 decimals.
   *
   * This might seem over-engineered, but it's the best solution we found to avoid
   * rounding errors and deliver a predictable outcome for the user while allowing
   * custom easing functions.
   */
  spreadByDuration(
    params: SpreadByDurationParams & {
      totalAmountHint: BigFraction;
      easingFunction: (val: number) => number;
    },
  ) {
    this.$model.projectionPeriods.length = 0;

    const startPeriodIndex = params.periodIndex;
    const checkTotal = makePortionsPerPeriod(
      params.duration,
      params.easingFunction,
    ).reduce(
      // eslint-disable-next-line max-params
      (sum, partOfTotal, idx, { length }) => {
        const isLast = idx + 1 === length;

        // Re-adjust the ratio to get a resulting amount that only has two decimal.
        // Doing so we might get some left over, so we make sure the last period
        // completes the sum.
        const amount =
          isLast ?
            params.totalAmountHint.sub(sum)
          : partOfTotal.mul(params.totalAmountHint).floor(2);

        const currentPeriodIndex = startPeriodIndex + idx;
        this._setPortionAtPeriodIndex(
          currentPeriodIndex,
          amount.div(params.totalAmountHint).toFraction(),
        );

        return sum.add(amount);
      },
      FRACTION_ZERO,
    );

    // sanity check, should never happen
    if (!checkTotal.equals(params.totalAmountHint)) {
      throw new Error(
        `The sum of the portions should be ${params.totalAmountHint.valueOf()} (found ${checkTotal.valueOf()})`,
      );
    }
  }
}
